import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import CheckpointCard from "./components/CheckpointCard";
import CheckpointModalAdmin from "./components/CheckpointModalAdmin";
import CheckpointModalTeam from "../../../teamView/components/CheckpointModalTeam";

const ProjectCalendar = ({
  projectId,
  roadmaps = [],
  submissions = [],
  teamMembers = [],
  isAdmin = false,
  currentUser,
}) => {
  
  // 🧭 Helper: local date-only string (YYYY-MM-DD)
  const toLocalDateKey = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // 🧾 Collect all submission days based on which checkpoint the user submitted for
const [submissionDays, setSubmissionDays] = useState(new Set());

React.useEffect(() => {
  if (!submissions?.length) return;

  const days = new Set();

  submissions.forEach((s) => {
    if (s.dateSubmittedFor) {
      // Parse safely and normalize into YYYY-MM-DD
      const [year, month, day] = s.dateSubmittedFor.split("-");
      const localKey = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
      days.add(localKey);
    }
  });

  console.log("✅ SubmissionDays from dateSubmittedFor:", Array.from(days));
  setSubmissionDays(days);
}, [submissions]);

  const now = new Date();
  const todayKey = toLocalDateKey(now);

  // State
  const [currentDate, setCurrentDate] = useState(now);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  // Start of week
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

  // === Build 7-day calendar ===
  const days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    const utcKey = toLocalDateKey(date);
    return {
      date,
      key: utcKey,
      label: date.toLocaleString("en-US", { weekday: "short" }).charAt(0),
      dayNum: date.getDate(),
      month: date.toLocaleString("en-US", { month: "short" }),
      year: date.getFullYear(),
      isToday: utcKey === todayKey,
    };
  });

  // Header label
  const firstDay = days[0];
  const lastDay = days[6];
  const headerLabel =
    firstDay.year === lastDay.year
      ? firstDay.month === lastDay.month
        ? `${firstDay.month} ${firstDay.year}`
        : `${firstDay.month} – ${lastDay.month} ${firstDay.year}`
      : `${firstDay.month} ${firstDay.year} – ${lastDay.month} ${firstDay.year}`;

  // === Expand checkpoints ===
  console.groupCollapsed("📅 Building expandedCheckpoints for ProjectCalendar");
  console.log("🧭 Incoming roadmaps:", roadmaps);
  const expandedCheckpoints = roadmaps
    .flatMap((phase) =>
      (phase.checkpoints || []).map((cp, index) => {
        const start = new Date(cp.startDate);
        const end = new Date(cp.endDate);
        const dates = [];
        const temp = new Date(start);
        while (temp <= end) {
          dates.push(toLocalDateKey(temp));
          temp.setUTCDate(temp.getUTCDate() + 1);
        }
        return dates.map((key) => ({
  ...cp,
  dateKey: key,

  // CRITICAL METADATA the backend requires
  phaseName: phase.title,
  phase: phase.title,
  checkpointIndex: index,
  index,

  // Unique ID for submissions
  checkpointId: `${phase.title}-${index}`,

  // Required for TeamModal pickup + submissions
  projectId: phase.projectId || projectId,
  roadmapId:
    phase?.roadmapId ||
    phase?.id ||
    phase?._id ||
    (roadmaps[0]?.roadmapId || roadmaps[0]?.id || null),

  // The calendar day the user clicks
  clickedDate: key,
}));

      })
    )
    .flat();

    console.log("✅ Expanded Checkpoints Sample:", expandedCheckpoints.slice(0, 5));
    console.groupEnd();

  // Navigation
  const handlePrev = () => {
    const newDate = new Date(startOfWeek);
    newDate.setDate(startOfWeek.getDate() - 7);
    setCurrentDate(newDate);
  };
  const handleNext = () => {
    const newDate = new Date(startOfWeek);
    newDate.setDate(startOfWeek.getDate() + 7);
    setCurrentDate(newDate);
  };
  const handleToday = () => setCurrentDate(now);

  return (
    <motion.div
      layout
      onClick={() => console.log("🧱 Outer container clicked")}
      animate={{ height: isExpanded ? 637 : 80 }}
      transition={{ type: "spring", stiffness: 150, damping: 18 }}
      className="w-full max-w-[1272px] bg-white rounded-[20px] shadow-md mx-auto 
                 overflow-hidden flex flex-col transition-all duration-500 mt-24 "
    >
      {/* HEADER */}
      <div className="flex items-center justify-between p-6">
        <div className="text-black text-2xl font-semibold">{headerLabel}</div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-4 py-1 rounded-full text-sm text-gray-500 hover:text-black transition"
          >
            {isExpanded ? "Hide Calendar" : "Expand Calendar"}
          </button>
          <div className="w-28 h-9 bg-zinc-400 rounded-[20px] opacity-40 cursor-not-allowed" />
        </div>
      </div>

      {/* VIEW */}
      {isExpanded && (
        <>
          <motion.div
            layout
            className="flex-1 flex items-start justify-center mb-6 relative px-6"
          >
            <motion.div layout className="flex h-full w-full">
              {days.map((day, idx) => {
                const dayCheckpoints = expandedCheckpoints.filter(
                  (cp) => cp.dateKey === day.key
                );

                return (
                  <motion.div
                    
                    key={idx}
                    layout
                    style={{ position: "relative", zIndex: 50, pointerEvents: "auto" }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    whileHover={{ scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 250, damping: 20 }}
                    className={`w-[calc(100%/7)] max-w-[160px] flex-shrink-0 
                      rounded-[20px] flex flex-col items-center p-3 cursor-pointer transition border
                      ${
                        day.isToday
                          ? "bg-blue-50 shadow-inner border-blue-300"
                          : "hover:bg-gray-50 border-transparent"
                      }`}

                    onClick={() => {
                      console.log(`🟢 Click detected on ${day.key}`);
                      if (!dayCheckpoints.length) {
                        console.log(`📅 ${day.key} → no checkpoints`);
                        return;
                      }
                      console.log(
                        `📅 ${day.key} → ${dayCheckpoints[0]?.checkpointId || "N/A"}`
                      );
                      setSelectedCheckpoint({
                        ...dayCheckpoints[0],
                        clickedDate: day.key,
                      });
                    }}
                  >
                    {/* Day label + number */}
                    <div className="text-stone-400 text-xl font-medium mb-2">
                      {day.label}
                    </div>
                    <div
                      className={`text-xl font-medium ${
                        day.isToday
                          ? "text-black font-bold text-2xl"
                          : "text-stone-400"
                      }`}
                    >
                      {day.dayNum}
                    </div>

                    {/* Checkpoints */}
                    <div className="mt-2 flex flex-col gap-2 items-center w-full">
                      {dayCheckpoints.map((cp, i) => (
                        <motion.div
                          key={i}
                          whileHover={{ scale: 1.03 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCheckpoint({
                              ...cp,
                              clickedDate: day.key,
                            });
                          }}
                        >
                          <CheckpointCard
                            checkpoint={cp}
                            hasSubmission={submissionDays.has(day.key)}
                          />
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>

          {/* FOOTER */}
          <div className="flex items-center justify-center gap-6 mt-auto text-sm text-gray-500 pb-4">
            <button className="hover:text-black" onClick={handlePrev}>
              ← Prev
            </button>
            <button className="font-semibold text-black" onClick={handleToday}>
              Today
            </button>
            <button className="hover:text-black" onClick={handleNext}>
              Next →
            </button>
          </div>
        </>
      )}

      {/* MODAL */}
      <AnimatePresence>
        {selectedCheckpoint && (
          <motion.div
            key="checkpoint-modal"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex justify-center items-center"
          >
            {isAdmin ? (
              <CheckpointModalAdmin
                checkpoint={selectedCheckpoint}
                onClose={() => setSelectedCheckpoint(null)}
                isAdmin={true}
                teamMembers={teamMembers}
                submissions={submissions}
              />

            ) : (
              <CheckpointModalTeam
                checkpoint={selectedCheckpoint}
                onClose={() => setSelectedCheckpoint(null)}
                teamMembers={teamMembers}
                currentUser={currentUser}
                submissions={submissions}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProjectCalendar;
